import { defineStore } from 'pinia'
import { api } from '@/services/api.js'
import { isNotificationsV2Enabled, syncPushDevice, currentPushEndpoint } from '@/services/pushDevice.js'
import { currentInstallContext, pushBanner, shouldBlockForPush } from '@/services/reachability.js'

/**
 * Reachability of this staff member and this install (#149), from
 * GET /api/subscriptions/status, for the banner (App.vue) and the "require
 * push" block (router guard, PushRequired view). Logic lives in
 * services/reachability.js; this store only gathers its inputs.
 *
 * refresh() re-syncs the push device first, so the server's answer reflects
 * this open. It runs on app open, after login and after push is turned on or
 * off. A failure keeps the last answer; with none yet, status stays null: no
 * banner and no block.
 */
// The refresh in flight, shared so the router guard waits on the same request.
let inflight = null

export const usePushReachabilityStore = defineStore('pushReachability', {
    state: () => ({
        enabled: false,
        status: null,
        permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
        install: currentInstallContext(),
        // Session-only dismissal, offered only when reachable on another device
        dismissed: false,
        loadedOnce: false
    }),
    getters: {
        banner(state) {
            return pushBanner({ enabled: state.enabled, status: state.status, install: state.install, permission: state.permission })
        },
        blocked(state) {
            return shouldBlockForPush({ enabled: state.enabled, status: state.status })
        }
    },
    actions: {
        async refresh() {
            this.loadedOnce = true
            const run = (async () => {
                try {
                    this.enabled = await isNotificationsV2Enabled()
                    this.install = currentInstallContext()
                    this.permission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
                    if (!this.enabled) {
                        this.status = null
                        return
                    }
                    await syncPushDevice().catch((e) => console.warn('[push device] sync failed', e?.message || e))
                    const endpoint = await currentPushEndpoint().catch(() => null)
                    const query = endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : ''
                    this.status = await api.get(`/api/subscriptions/status${query}`, { headers: { 'Cache-Control': 'no-store' } })
                } catch (e) {
                    // Keep the last answer: a blip must not lift a block (or raise a banner)
                    console.warn('[push reachability] status unavailable', e?.message || e)
                }
            })()
            inflight = run
            try {
                await run
            } finally {
                if (inflight === run) inflight = null
            }
        },

        /** For the router guard: the first navigation of a session waits for one answer. */
        async ensureLoaded() {
            if (inflight) return inflight
            if (!this.loadedOnce) await this.refresh()
        },

        reset() {
            this.status = null
            this.dismissed = false
            this.loadedOnce = false
        }
    }
})
